<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { projectStore } from '../../stores/project';
  import { clientStore } from '../../stores/client';

  export let project = null;
  export let initialClient = null;

  const dispatch = createEventDispatcher();

  let formData = {
    title: project?.title || '',
    client_id: project?.client || initialClient || '',
    status: project?.status || 'PLANNING',
    description: project?.description || ''
  };

  let clients = [];
  let loading = false;
  let error = '';

  const statusOptions = [
    { value: 'PLANNING', label: 'Planning' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  onMount(async () => {
    try {
      await clientStore.load();
      clients = $clientStore.items || [];
    } catch (e) {
      // Failed to load clients
    }
  });

  async function handleSubmit() {
    if (!formData.title.trim()) {
      error = 'Title is required';
      return;
    }

    loading = true;
    error = '';

    try {
      const data = {
        title: formData.title.trim(),
        status: formData.status,
        description: formData.description.trim() || null
      };

      if (formData.client_id) {
        data.client = formData.client_id;
      }

      if (project?.id) {
        await projectStore.update(project.id, data);
      } else {
        await projectStore.add(data);
      }
      dispatch('close');
    } catch (e) {
      error = e.message || 'Failed to save';
    } finally {
      loading = false;
    }
  }
</script>

<div class="form-container">
  <div class="form-header">
    <h2>{project ? 'Edit Project' : 'New Project'}</h2>
    <button class="btn btn-secondary" on:click={() => dispatch('close')}>Cancel</button>
  </div>

  {#if error}
    <div class="form-error">{error}</div>
  {/if}

  <form on:submit|preventDefault={handleSubmit}>
    <div class="form-group">
      <label for="title">Title <span class="required">*</span></label>
      <input id="title" type="text" bind:value={formData.title} placeholder="Project title" required />
    </div>

    <div class="form-row">
      <div class="form-group">
        <label for="client">Client</label>
        <select id="client" bind:value={formData.client_id}>
          <option value="">Select client...</option>
          {#each clients as c}
            <option value={c.id}>{c.name}</option>
          {/each}
        </select>
      </div>
      <div class="form-group">
        <label for="status">Status</label>
        <select id="status" bind:value={formData.status}>
          {#each statusOptions as opt}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      </div>
    </div>

    <div class="form-group">
      <label for="description">Description</label>
      <textarea id="description" bind:value={formData.description} rows="4" placeholder="Project description..."></textarea>
    </div>

    <div class="form-actions">
      <button type="submit" class="btn btn-primary" disabled={loading}>
        {loading ? 'Saving...' : 'Save Project'}
      </button>
    </div>
  </form>
</div>

<style>
  .form-container {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  .form-header h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .form-error {
    margin: 16px 20px 0;
    padding: 12px;
    background: #fee2e2;
    color: #dc2626;
    border-radius: var(--radius);
    font-size: 13px;
  }

  form {
    padding: 20px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px;
    color: var(--text);
  }

  .required {
    color: var(--danger);
  }

  input, select, textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 14px;
    background: #fff;
  }

  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: var(--accent);
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }

  .form-actions {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }
</style>
